import React, { useEffect, useState } from "react";
import { Container, Label, Input, FormGroup, Button, Row, Col, Badge, Table } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  GetBusinessSettings,
  UpdateBusinessSettings,
  GetPaymentGatewayStatus,
  GetAuditLog,
} from "../../../requests";
import { toast } from "react-toastify";
import classes from "./styles.module.css";

const ACTION_LABELS = {
  LOGIN: "Login",
  USER_STATUS_CHANGE: "User Status Changed",
  PASSWORD_RESET: "Password Reset",
  ORDER_DELETE: "Order Deleted",
  ORDER_REFUND: "Order Refunded",
  RIDER_DISABLE: "Rider Disabled",
  RIDER_ENABLE: "Rider Enabled",
  RIDER_PASSWORD_RESET: "Rider Password Reset",
  STORE_ADMIN_ASSIGNED: "Store Admin Assigned",
  BUSINESS_SETTINGS_UPDATED: "Business Settings Updated",
};

const tabs = [
  { id: "payment", label: "Payment Gateway", icon: "mdi mdi-credit-card-outline" },
  { id: "business", label: "Business Info", icon: "mdi mdi-domain" },
  { id: "activity", label: "Activity Log", icon: "mdi mdi-history" },
];

// ─── Payment Gateway Tab ────────────────────────────────────────────────────
const PaymentGatewayTab = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const resp = await GetPaymentGatewayStatus(token);
        setStatus(resp.data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load payment gateway status");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <LoadingSpinner loading={true} />;
  if (!status) return null;

  return (
    <div className={classes.card}>
      <h5 className="mb-3">Razorpay</h5>
      <Row className="align-items-center mb-3">
        <Col md={3} className="text-muted">Mode</Col>
        <Col md={9}>
          <Badge color={status.mode === "live" ? "success" : "warning"} pill style={{ padding: "6px 14px" }}>
            {status.mode === "live" ? "LIVE" : "TEST"}
          </Badge>
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={3} className="text-muted">Key ID</Col>
        <Col md={9}><code>{status.keyIdPreview || "Not configured"}</code></Col>
      </Row>
      {status.isUsingFallbackKeys && (
        <div className="alert alert-warning mb-0">
          <strong>Using hardcoded test-mode keys.</strong> No <code>RAZORPAY_KEY_ID</code> /{" "}
          <code>RAZORPAY_KEY_SECRET</code> is set in the server's environment, so it's falling back to
          the test keys baked into <code>config/env.js</code>. Real payments will not work until live
          keys are set there.
        </div>
      )}
      <p className="text-muted small mt-3 mb-0">
        Keys are configured via the server's environment variables and require a redeploy to change —
        not editable here, to avoid exposing payment secrets through a web form.
      </p>
    </div>
  );
};

// ─── Business Info Tab ──────────────────────────────────────────────────────
const BusinessInfoTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [address, setAddress] = useState("");
  const [termsUrl, setTermsUrl] = useState("");
  const [privacyUrl, setPrivacyUrl] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const resp = await GetBusinessSettings(token);
      const data = resp.data;
      setBusinessName(data.businessName || "");
      setSupportPhone(data.supportPhone || "");
      setSupportEmail(data.supportEmail || "");
      setGstNumber(data.gstNumber || "");
      setAddress(data.address || "");
      setTermsUrl(data.termsUrl || "");
      setPrivacyUrl(data.privacyUrl || "");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load business settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await UpdateBusinessSettings(token, {
        businessName, supportPhone, supportEmail, gstNumber, address, termsUrl, privacyUrl,
      });
      toast.success("Business settings updated successfully");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error?.message || "Failed to update business settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner loading={true} />;

  return (
    <div className={classes.card}>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label for="businessName">Business Name</Label>
            <Input id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="gstNumber">GST Number</Label>
            <Input id="gstNumber" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="supportPhone">Support Phone</Label>
            <Input id="supportPhone" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="supportEmail">Support Email</Label>
            <Input type="email" id="supportEmail" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={12}>
          <FormGroup>
            <Label for="address">Business Address</Label>
            <Input type="textarea" id="address" value={address} onChange={(e) => setAddress(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="termsUrl">Terms & Conditions URL</Label>
            <Input id="termsUrl" placeholder="https://..." value={termsUrl} onChange={(e) => setTermsUrl(e.target.value)} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="privacyUrl">Privacy Policy URL</Label>
            <Input id="privacyUrl" placeholder="https://..." value={privacyUrl} onChange={(e) => setPrivacyUrl(e.target.value)} />
          </FormGroup>
        </Col>
      </Row>
      <Button color="primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );
};

// ─── Activity Log Tab ───────────────────────────────────────────────────────
const ActivityLogTab = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [actionFilter, setActionFilter] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const resp = await GetAuditLog(token, actionFilter ? { action: actionFilter } : {});
      setEntries(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const formatTime = (ts) => {
    if (!ts) return "—";
    const date = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return date.toLocaleString();
  };

  return (
    <div className={classes.card}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div style={{ width: "260px" }}>
          <Input type="select" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Input>
        </div>
        <Button color="light" size="sm" onClick={load}>
          <i className="mdi mdi-refresh me-1" /> Refresh
        </Button>
      </div>
      {loading ? (
        <LoadingSpinner loading={true} />
      ) : entries.length === 0 ? (
        <p className="text-muted text-center py-4 mb-0">No activity recorded yet.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Role</th>
                <th>Action</th>
                <th>Target</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatTime(entry.timestamp)}</td>
                  <td>{entry.actorName || entry.actorId || "—"}</td>
                  <td className="text-capitalize">{entry.actorRole || "—"}</td>
                  <td>{ACTION_LABELS[entry.action] || entry.action}</td>
                  <td>
                    {entry.targetType && entry.targetId
                      ? `${entry.targetType}: ${entry.targetId}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const AdminSettings = () => {
  document.title = "Settings | Susipizza";
  const [activeTab, setActiveTab] = useState("payment");

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Administration" breadcrumbItem="Settings" />

          <div className={classes.tabContainer}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${classes.tabItem} ${activeTab === tab.id ? classes.tabItemActive : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon} style={{ fontSize: "1.1rem" }}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "payment" && <PaymentGatewayTab />}
          {activeTab === "business" && <BusinessInfoTab />}
          {activeTab === "activity" && <ActivityLogTab />}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdminSettings;
